<template>
  <button
    :id="id"
    :type="buttonType"
    :class="['btn', `btn-${variant}`, size ? `btn-${size}` : '']"
    :disabled="loading || disabled"
    @click="$emit('click', $event)"
    class="generic-button"
  >
    <span v-if="showDots" class="generic-button-label-placeholder"><slot /></span>
    <slot v-else />

    <Spinner v-if="loading && !showDots" :size="30" :loader="true" color />

    <span v-if="showDots" class="generic-button-dots" role="status" aria-label="Loading">
      <span class="generic-button-dot"></span>
      <span class="generic-button-dot"></span>
      <span class="generic-button-dot"></span>
    </span>
  </button>
</template>

<script>
// This component is created as a temporary solution to the outdated vue-promise-btn package
// which does not support Vue 2.7.

import Spinner from '@/components/Spinner.vue'

export default {
  components: { Spinner },
  props: {
    id: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean
    },
    loading: {
      type: Boolean
    },
    loadingStyle: {
      default: 'spinner',
      validator(value) {
        return ['spinner', 'dots'].includes(value)
      }
    },
    variant: {
      default: 'primary',
      validator(value) {
        return ['primary', 'secondary'].includes(value)
      }
    },
    size: {
      validator(value) {
        return !value || ['xs', 'sm', 'lg'].includes(value)
      }
    },
    buttonType: {
      default: 'button',
      validator(value) {
        return ['button', 'submit', 'reset'].includes(value)
      }
    }
  },
  computed: {
    showDots() {
      return this.loading && this.loadingStyle === 'dots'
    }
  }
}
</script>

<style scoped>
.generic-button {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 8px;
}

.generic-button-label-placeholder {
  visibility: hidden;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.generic-button-dots {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.generic-button-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
  opacity: 0.35;
  animation: generic-button-dot-pulse 1.4s ease-in-out infinite;
}

.generic-button-dot:nth-child(2) {
  animation-delay: 0.16s;
}

.generic-button-dot:nth-child(3) {
  animation-delay: 0.32s;
}

@keyframes generic-button-dot-pulse {
  0%,
  60%,
  100% {
    opacity: 0.35;
  }

  30% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .generic-button-dot {
    animation: none;
    opacity: 0.7;
  }
}
</style>
